import {
  DocumentStatus,
  EmployeeBeneficiaryStatus,
  EmployeeBeneficiaryValidationStatus,
} from '@cpce/shared';
import { useSetJustificatifValidationStatus } from '@/api/beneficiaries/beneficiaries.mutations';
import { useExportEmployeeBeneficiaries } from '@/api/employees/employees.downloads';
import type {
  EmployeeBeneficiaryFormatted,
  EmployeeWithBeneficiariesFormatted,
} from '@/api/employees/employees.types';
import {
  useSetEmployeeBeneficiaryLienType,
  useSetEmployeeBeneficiaryValidationStatus,
} from '@/api/employees-beneficiaries/employees-beneficiaries.mutations';
import { Button } from '@/components/Button';
import { ExportButton } from '@/components/ExportButton';
import { FieldsGrid } from '@/components/FieldsGrid';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/Select';
import { ValidationStatusBadge } from '@/components/ValidationStatusBadge';
import { formatDate } from '@/utils/date.utils';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { BeneficiaryDocumentsCard } from '../../components/BeneficiaryDocumentsCard';
import { ACharge } from '../../employees-beneficiaries/columns/employees-beneficiaries-columns';
import { employeeBeneficiaryStatusToString } from '../utils/employees.utils';

interface EmployeeBeneficiariesProps {
  employee: EmployeeWithBeneficiariesFormatted;
}

export function EmployeeBeneficiaries(props: EmployeeBeneficiariesProps) {
  const { employee } = props;
  const { download: exportEmployeeBeneficiaries } = useExportEmployeeBeneficiaries(employee.id);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 justify-between">
        <span className="text-lg font-medium">{`Ayants-droit (${employee.employeeBeneficiaries.length}) et documents justificatifs`}</span>
        <ExportButton onClick={exportEmployeeBeneficiaries} />
      </div>
      {employee.employeeBeneficiaries.map((employeeBeneficiary: EmployeeBeneficiaryFormatted) => (
        <EmployeeBeneficiaryCard
          key={employeeBeneficiary.id}
          employeeBeneficiary={employeeBeneficiary}
          externalEmployeeId={employee.id}
        />
      ))}
    </div>
  );
}

interface EmployeeBeneficiaryCardProps {
  employeeBeneficiary: EmployeeBeneficiaryFormatted;
  externalEmployeeId: number;
}

function EmployeeBeneficiaryCard(props: EmployeeBeneficiaryCardProps) {
  const { employeeBeneficiary, externalEmployeeId } = props;

  const beneficiary = employeeBeneficiary.beneficiary;
  const justificatifs = beneficiary.externalJustificatifs ?? [];
  const navigate = useCommiteeSlugNavigate();
  const hasAlert =
    justificatifs.some((doc) => doc.status === DocumentStatus.awaitingValidation) ||
    employeeBeneficiary.validationStatus === EmployeeBeneficiaryValidationStatus.awaitingValidation;

  const {
    mutate: setEmployeeBeneficiaryValidationStatus,
    isPending: isSettingValidationStatus,
    variables: pendingValidation,
  } = useSetEmployeeBeneficiaryValidationStatus(employeeBeneficiary.beneficiary.id);
  const { mutate: setLienType } = useSetEmployeeBeneficiaryLienType(
    employeeBeneficiary.beneficiary.id
  );
  const { mutate: setJustificatifValidationStatus } = useSetJustificatifValidationStatus();
  return (
    <BeneficiaryDocumentsCard
      title={employeeBeneficiary.beneficiary.fullName || ''}
      key={employeeBeneficiary.id}
      beneficiary={beneficiary}
      justificatifs={justificatifs}
      onDocumentStatusChange={(justificatif, validated) =>
        setJustificatifValidationStatus({
          justificatifId: justificatif.id,
          validated,
          externalEmployeeId,
          documentName: justificatif.type,
        })
      }
      hasAlert={hasAlert}
      titleActions={
        <>
          <ValidationStatusBadge status={employeeBeneficiary.validationStatus} />
          {employeeBeneficiary.validationStatus ===
            EmployeeBeneficiaryValidationStatus.awaitingValidation && (
            <>
              <Button
                variant="color"
                className="bg-green-600"
                disabled={isSettingValidationStatus}
                isPending={isSettingValidationStatus && pendingValidation}
                onClick={() => setEmployeeBeneficiaryValidationStatus(true)}
              >
                Valider
              </Button>
              <Button
                variant="color"
                className="bg-red-600"
                disabled={isSettingValidationStatus}
                isPending={isSettingValidationStatus && !pendingValidation}
                onClick={() => setEmployeeBeneficiaryValidationStatus(false)}
              >
                Refuser
              </Button>
            </>
          )}
        </>
      }
      redirect={{
        text: "Voir l'ayant droit",
        onRedirect: () => {
          navigate({
            to: '/$commiteeSlug/beneficiaries/employees-beneficiaries/{-$id}',
            params: { id: employeeBeneficiary.beneficiary.id.toString() },
          });
        },
      }}
    >
      <FieldsGrid
        fields={[
          {
            title: 'Lien',
            value: (
              <Select
                value={employeeBeneficiary.status}
                onValueChange={(value) => setLienType(value as EmployeeBeneficiaryStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EmployeeBeneficiaryStatus.married}>
                    {employeeBeneficiaryStatusToString(EmployeeBeneficiaryStatus.married)}
                  </SelectItem>
                  <SelectItem value={EmployeeBeneficiaryStatus.child}>
                    {employeeBeneficiaryStatusToString(EmployeeBeneficiaryStatus.child)}
                  </SelectItem>
                </SelectContent>
              </Select>
            ),
          },
          {
            title: 'À charge',
            value: <ACharge value={beneficiary.aCharge} />,
          },
          {
            title: 'Date de naissance',
            value: formatDate(beneficiary.birthDate),
          },
          {
            title: 'Genre',
            value: beneficiary.genderFormatted,
          },
        ]}
      />
    </BeneficiaryDocumentsCard>
  );
}
